import {
  DropdownSelectItemType,
  FieldWrapper,
  Select,
  SelectProps,
} from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';

import {
  HookFormFieldWrappedBaseProps,
  useHookFormFieldWrapper,
} from 'utils/hookForm';

/**
 * Represents the React Hook Form variant of the {@link Select} component.
 *
 * @deprecated Use {@link HookFormSelectionList} instead.
 */
export const HookFormSelect = <
  TItemType extends DropdownSelectItemType,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: HookFormFieldWrappedBaseProps<
    TFieldValues,
    TName,
    SelectProps<TItemType>
  >,
): JSX.Element => {
  const { controller, controllerProps, fieldWrapperProps } =
    useHookFormFieldWrapper(props);

  const [selectedItems, setSelectedItems] = useState<TItemType[]>(
    // Note that if `defaultValues` is set on `useForm`, the consumer of this
    // component has to set `selectedItems` manually seeing how we can not
    // extract that data from here.
    ((controllerProps.defaultValue ??
      props.componentProps.selectedItems) as TItemType[]) ?? [],
  );

  /** Handles the item selection. */
  const handleChange = useCallback(
    (item: TItemType, isChecked: boolean) => {
      setSelectedItems(
        isChecked
          ? selectedItems.concat(item)
          : selectedItems.filter(({ id }) => id !== item.id),
      );
    },
    [selectedItems],
  );

  /** Handles selecting an single item. */
  const handleSelect = (item: TItemType) => setSelectedItems([item]);

  /** Handles clearing the selected items. */
  const handleClear = () => setSelectedItems([]);

  // Handles calling React Hook Form change event because the select component
  // doesn't conform to what.
  useUpdateEffect(() => {
    props.componentProps.onSelect?.(selectedItems);
    props.componentProps.onChange?.(selectedItems);
  }, [controller.field.onChange, selectedItems]);

  const meta = {
    ...fieldWrapperProps.meta,
    // When the form is submitted without a selected item, the select component
    // stays unblurred and therefore untouched. The FieldWrapper and the
    // InputWrapper inside the Select don't accept a 'meta.error' message when
    // 'touched' is 'false'. Overwrite it here to be either 'true' or
    // 'undefined' fixes this. This way we get the alert message and the colored
    // border.
    touched: fieldWrapperProps.meta?.touched ? true : undefined,
  };

  return (
    <FieldWrapper {...fieldWrapperProps} meta={meta}>
      <Select
        {...props.componentProps}
        {...controller.field}
        id={controllerProps.name}
        selectedItems={selectedItems}
        disabled={fieldWrapperProps.disabled}
        onChange={handleChange}
        onClear={handleClear}
        onSelect={handleSelect}
        status={FieldWrapper.getStatus(meta)}
      />
    </FieldWrapper>
  );
};
